"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  Check,
  Code,
  Eye,
  FileText,
  Server,
  FolderOpen,
  File,
  HardDrive,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getAuthTokenAsync } from "@/lib/auth";
import { normalizeBackendUrl } from "@/lib/api-config";
import type { ConnectionConfig } from "../connection-studio";

type ResponseViewMode = "raw" | "preview" | "html";

interface TestTabProps {
  formData: ConnectionConfig;
}

interface TestResult {
  success: boolean;
  status_code?: number;
  response_time_ms?: number;
  headers?: Record<string, string>;
  body?: unknown;
  error?: string;
  details?: {
    server?: string;
    share?: string;
    root_dirs?: number;
    root_files?: number;
    sample_entries?: string[];
  };
}

const CONNECTION_TYPE_LABELS: Record<string, string> = {
  rest_api: "REST API",
  graphql: "GraphQL",
  database: "Database",
  file_share: "File Share (SMB)",
  webhook: "Webhook",
  custom: "Custom",
};

function detectContentType(headers?: Record<string, string>): string {
  if (!headers) return "unknown";
  const ct = Object.entries(headers).find(
    ([k]) => k.toLowerCase() === "content-type",
  )?.[1] ?? "";
  if (ct.includes("html")) return "html";
  if (ct.includes("json")) return "json";
  if (ct.includes("xml")) return "xml";
  if (ct.includes("text")) return "text";
  return "unknown";
}

function isHtmlString(body: unknown): boolean {
  if (typeof body !== "string") return false;
  const trimmed = body.trim();
  return trimmed.startsWith("<!") || trimmed.startsWith("<html") || trimmed.startsWith("<HTML");
}

export function TestTab({ formData }: TestTabProps) {
  const [method, setMethod] = useState("GET");
  const [path, setPath] = useState("");
  const [requestBody, setRequestBody] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<ResponseViewMode>("raw");

  const isFileShare = formData.type === "file_share";

  const handleTest = async () => {
    if (!isFileShare && !formData.base_url && !["database"].includes(formData.type)) {
      setResult({ success: false, error: "Base URL is required" });
      return;
    }

    if (isFileShare && !formData.config?.server) {
      setResult({ success: false, error: "Server address is required. Configure it in the Auth tab." });
      return;
    }

    if (isFileShare && !formData.config?.share_name) {
      setResult({ success: false, error: "Share name is required. Configure it in the Auth tab." });
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const authToken = await getAuthTokenAsync();
      const API_URL = normalizeBackendUrl(
        process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_BACKEND_URL
      );

      const testConfig = isFileShare
        ? {
            connection_type: "file_share",
            auth_type: formData.auth_type,
            auth_config: formData.auth_config,
            config: formData.config,
          }
        : {
            connection_type: formData.type,
            base_url: formData.base_url,
            path: path || "/",
            method,
            auth_type: formData.auth_type,
            auth_config: formData.auth_config,
            headers: formData.headers,
            body: requestBody ? JSON.parse(requestBody) : undefined,
            config: formData.config,
          };

      const response = await fetch(`${API_URL}/v1/connections/test-config`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(testConfig),
      });

      const data = await response.json();
      setResult(data);

      if (!isFileShare) {
        const ct = detectContentType(data.headers);
        if (ct === "html" || isHtmlString(data.body)) {
          setViewMode("html");
        } else {
          setViewMode("raw");
        }
      }
    } catch (error) {
      console.error("Test error:", error);
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Test failed",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    if (result?.body) {
      navigator.clipboard.writeText(
        typeof result.body === "string"
          ? result.body
          : JSON.stringify(result.body, null, 2)
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const fullUrl = formData.base_url
    ? `${formData.base_url}${path.startsWith("/") ? path : `/${path}`}`
    : "";

  if (isFileShare) {
    return <SMBTestView formData={formData} isLoading={isLoading} result={result} onTest={handleTest} />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white">Test Connection</h3>
        <p className="text-sm text-white/50">
          Send a test request to verify your configuration
        </p>
      </div>

      {/* Request Builder */}
      <div className="space-y-4">
        <div className="flex gap-3">
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="w-28 border-white/10 bg-white/5 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GET">GET</SelectItem>
              <SelectItem value="POST">POST</SelectItem>
              <SelectItem value="PUT">PUT</SelectItem>
              <SelectItem value="PATCH">PATCH</SelectItem>
              <SelectItem value="DELETE">DELETE</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex-1">
            <Input
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="/endpoint/path"
              className="border-white/10 bg-white/5 font-mono text-white placeholder:text-white/40"
            />
          </div>

          <Button
            onClick={handleTest}
            disabled={isLoading || !formData.base_url}
            className="bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Send
          </Button>
        </div>

        {fullUrl && (
          <div className="rounded-md bg-white/5 px-3 py-2">
            <span className="text-xs text-white/40">Full URL: </span>
            <span className="font-mono text-xs text-white/80">{fullUrl}</span>
          </div>
        )}

        {["POST", "PUT", "PATCH"].includes(method) && (
          <div className="space-y-2">
            <Label className="text-white/80">Request Body (JSON)</Label>
            <Textarea
              value={requestBody}
              onChange={(e) => setRequestBody(e.target.value)}
              placeholder='{"key": "value"}'
              className="min-h-[100px] border-white/10 bg-white/5 font-mono text-sm text-white placeholder:text-white/40"
            />
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {result.success ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              ) : (
                <XCircle className="h-5 w-5 text-rose-400" />
              )}
              <span className="font-medium text-white">
                {result.success ? "Success" : "Failed"}
              </span>
              {result.status_code && (
                <Badge
                  variant="outline"
                  className={
                    result.status_code >= 200 && result.status_code < 300
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                      : result.status_code >= 400
                      ? "border-rose-500/30 bg-rose-500/10 text-rose-400"
                      : "border-amber-500/30 bg-amber-500/10 text-amber-400"
                  }
                >
                  {result.status_code}
                </Badge>
              )}
            </div>
            {result.response_time_ms && (
              <div className="flex items-center gap-1 text-sm text-white/50">
                <Clock className="h-4 w-4" />
                {result.response_time_ms}ms
              </div>
            )}
          </div>

          {result.error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-4">
              <p className="text-sm text-rose-200">{result.error}</p>
            </div>
          )}

          {result.body !== undefined && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-white/80">Response Body</Label>
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5 rounded-md border border-white/10 bg-white/5 p-0.5">
                    <button
                      type="button"
                      onClick={() => setViewMode("raw")}
                      className={cn(
                        "flex items-center gap-1 rounded px-2 py-1 text-[13px] transition-colors",
                        viewMode === "raw"
                          ? "bg-white/10 text-white"
                          : "text-white/40 hover:text-white/60",
                      )}
                    >
                      <Code className="h-3 w-3" />
                      Raw
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode("preview")}
                      className={cn(
                        "flex items-center gap-1 rounded px-2 py-1 text-[13px] transition-colors",
                        viewMode === "preview"
                          ? "bg-white/10 text-white"
                          : "text-white/40 hover:text-white/60",
                      )}
                    >
                      <Eye className="h-3 w-3" />
                      Preview
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode("html")}
                      className={cn(
                        "flex items-center gap-1 rounded px-2 py-1 text-[13px] transition-colors",
                        viewMode === "html"
                          ? "bg-white/10 text-white"
                          : "text-white/40 hover:text-white/60",
                      )}
                    >
                      <FileText className="h-3 w-3" />
                      HTML
                    </button>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="h-8 text-white/50 hover:text-white"
                  >
                    {copied ? (
                      <Check className="mr-1 h-4 w-4" />
                    ) : (
                      <Copy className="mr-1 h-4 w-4" />
                    )}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>

              {viewMode === "html" ? (
                <div className="max-h-[400px] overflow-auto rounded-lg border border-white/10 bg-white">
                  <iframe
                    srcDoc={typeof result.body === "string" ? result.body : JSON.stringify(result.body, null, 2)}
                    sandbox=""
                    title="Response preview"
                    className="h-[380px] w-full border-0"
                  />
                </div>
              ) : viewMode === "preview" ? (
                <div className="max-h-[400px] overflow-auto rounded-lg border border-white/10 bg-white p-4">
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html:
                        typeof result.body === "string"
                          ? result.body
                          : `<pre>${JSON.stringify(result.body, null, 2)}</pre>`,
                    }}
                  />
                </div>
              ) : (
                <div className="max-h-[400px] overflow-auto rounded-lg border border-white/10 bg-slate-950 p-4">
                  <pre className="font-mono text-sm text-white/80 whitespace-pre-wrap">
                    {typeof result.body === "string"
                      ? result.body
                      : JSON.stringify(result.body, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {result.headers && Object.keys(result.headers).length > 0 && (
            <div className="space-y-2">
              <Label className="text-white/80">Response Headers</Label>
              <div className="rounded-lg border border-white/10 bg-slate-950 p-4">
                {Object.entries(result.headers).map(([key, value]) => (
                  <div key={key} className="font-mono text-xs">
                    <span className="text-emerald-400">{key}</span>
                    <span className="text-white/40">: </span>
                    <span className="text-white/60">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Configuration Preview */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-4">
        <h4 className="text-sm font-medium text-white/80">Current Configuration</h4>
        <div className="mt-3 space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-white/50">Connection Type</span>
            <span className="text-white/80">{CONNECTION_TYPE_LABELS[formData.type] || formData.type}</span>
          </div>
          {formData.type === "database" ? (
            <>
              <div className="flex justify-between">
                <span className="text-white/50">Database</span>
                <span className="font-mono text-white/80">
                  {(formData.config?.db_type as string) || "postgresql"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Host</span>
                <span className="font-mono text-white/80">
                  {formData.config?.connection_string
                    ? "Via connection string"
                    : `${(formData.config?.host as string) || "Not set"}:${(formData.config?.port as number) || ""}`}
                </span>
              </div>
            </>
          ) : (
            <div className="flex justify-between">
              <span className="text-white/50">Base URL</span>
              <span className="font-mono text-white/80">{formData.base_url || "Not set"}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-white/50">Auth Type</span>
            <span className="text-white/80">{formData.auth_type}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Custom Headers</span>
            <span className="text-white/80">{Object.keys(formData.headers || {}).length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* SMB-specific test view                                              */
/* ------------------------------------------------------------------ */

interface SMBTestViewProps {
  formData: ConnectionConfig;
  isLoading: boolean;
  result: TestResult | null;
  onTest: () => void;
}

function SMBTestView({ formData, isLoading, result, onTest }: SMBTestViewProps) {
  const config = formData.config || {};
  const serverAddr = (config.server as string) || "";
  const shareName = (config.share_name as string) || "";
  const port = (config.port as number) || 445;
  const basePath = (config.base_path as string) || "";
  const username = (formData.auth_config?.username as string) || "";
  const domain = (config.domain as string) || "WORKGROUP";

  const uncPath = serverAddr
    ? `\\\\${serverAddr}\\${shareName}${basePath ? `\\${basePath.replace(/\//g, "\\")}` : ""}`
    : "";

  const canTest = !!serverAddr && !!shareName;

  const smbDetails = result?.details || (result?.body as TestResult["details"]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white">Test SMB Connection</h3>
        <p className="text-sm text-white/50">
          Verify connectivity to the file share and list root contents
        </p>
      </div>

      {/* Connection summary */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-white/80">
          <HardDrive className="h-4 w-4 text-cyan-400" />
          Connection Details
        </div>
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-white/50">Server</span>
            <span className="font-mono text-white/80">{serverAddr || "Not set"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Port</span>
            <span className="font-mono text-white/80">{port}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Share</span>
            <span className="font-mono text-white/80">{shareName || "Not set"}</span>
          </div>
          {basePath && (
            <div className="flex justify-between">
              <span className="text-white/50">Base Path</span>
              <span className="font-mono text-white/80">{basePath}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-white/50">Domain</span>
            <span className="font-mono text-white/80">{domain}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50">Username</span>
            <span className="font-mono text-white/80">{username || "Not set"}</span>
          </div>
        </div>
        {uncPath && (
          <div className="mt-2 rounded-md bg-white/5 px-3 py-2">
            <span className="text-xs text-white/40">UNC Path: </span>
            <span className="font-mono text-xs text-white/80">{uncPath}</span>
          </div>
        )}
      </div>

      {/* Test button */}
      <Button
        onClick={onTest}
        disabled={isLoading || !canTest}
        className="w-full bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
      >
        {isLoading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Play className="mr-2 h-4 w-4" />
        )}
        {isLoading ? "Connecting..." : "Test Connection"}
      </Button>

      {!canTest && !result && (
        <p className="text-center text-xs text-amber-400/80">
          Configure server address and share name in the Auth tab before testing.
        </p>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Status banner */}
          <div className={cn(
            "flex items-center justify-between rounded-lg border p-4",
            result.success
              ? "border-emerald-500/30 bg-emerald-500/10"
              : "border-rose-500/30 bg-rose-500/10",
          )}>
            <div className="flex items-center gap-3">
              {result.success ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              ) : (
                <XCircle className="h-5 w-5 text-rose-400" />
              )}
              <span className={cn("font-medium", result.success ? "text-emerald-300" : "text-rose-300")}>
                {result.success ? "Connection Successful" : "Connection Failed"}
              </span>
            </div>
            {result.response_time_ms != null && (
              <div className="flex items-center gap-1 text-sm text-white/50">
                <Clock className="h-4 w-4" />
                {result.response_time_ms}ms
              </div>
            )}
          </div>

          {result.error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-4">
              <p className="text-sm text-rose-200">{result.error}</p>
            </div>
          )}

          {/* SMB result details */}
          {result.success && smbDetails && (
            <div className="space-y-4">
              {/* Share stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <FolderOpen className="h-4 w-4 text-amber-400" />
                    <span className="text-2xl font-semibold text-white">
                      {smbDetails.root_dirs ?? 0}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-white/50">Directories</p>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <File className="h-4 w-4 text-cyan-400" />
                    <span className="text-2xl font-semibold text-white">
                      {smbDetails.root_files ?? 0}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-white/50">Files</p>
                </div>
              </div>

              {/* Sample entries */}
              {smbDetails.sample_entries && smbDetails.sample_entries.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-white/80">Root Entries</Label>
                  <div className="max-h-[260px] overflow-auto rounded-lg border border-white/10 bg-slate-950 p-3">
                    <div className="space-y-1">
                      {smbDetails.sample_entries.map((entry) => (
                        <div key={entry} className="flex items-center gap-2 font-mono text-xs text-white/70">
                          <Server className="h-3 w-3 shrink-0 text-white/30" />
                          {entry}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
